import serviceLandingFactory from '@rover/react-lib/src/factories/serviceLandingFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { get } from '@rover/react-lib/src/pages/landing/ServiceLanding/serviceLanding.api';
import generateActions from '@rover/react-lib/src/redux/ducks/generateActions';
import { effects as serviceLinksEffects } from '@rover/react-lib/src/redux/ducks/serviceLinks.duck';
import { ServiceLanding } from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import getPath from '@rover/shared/js/utils/getPath';
import {
  COUNTRY_URL_PREFIXES_REGEX_PATTERN,
  getCountryUrlPrefix,
} from '@rover/utilities/roverGeo.constants';

export type State = {
  loading: boolean;
  errorMessage: string;
  fetchServiceLandingResponse: ServiceLanding;
};

type Actions = {
  fetchServiceLandingPending: ActionCreator<void>;
  fetchServiceLandingFailure: ActionCreator<string>;
  fetchServiceLandingSuccess: ActionCreator<ServiceLanding>;
};

type Effects = {
  fetchServiceLanding: EffectCreator<void>;
  earlyPrefetch: EffectCreator<void>;
};

type Selectors = {
  getServiceLanding: SelectorCreator<ServiceLanding>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

const SERVICE_LANDING_REGEX = new RegExp(
  `^(?:/(?<country_url_prefix>${COUNTRY_URL_PREFIXES_REGEX_PATTERN}))?/(?<service_landing_slug>[a-z-]+)/`
);

export const options: Options = {
  name: 'serviceLanding',
  initialState: {
    loading: false,
    errorMessage: '',
    fetchServiceLandingResponse: serviceLandingFactory(),
  },
  actions: {
    ...generateActions('fetchServiceLanding'),
  },
  effects: {
    fetchServiceLanding:
      () =>
      async (
        dispatch,
        getState,
        { fetchServiceLandingPending, fetchServiceLandingSuccess, fetchServiceLandingFailure }
      ) => {
        dispatch(fetchServiceLandingPending());

        try {
          const path = getPath();
          const countryUrlPrefix = getCountryUrlPrefix(path);
          const translatedServiceLandingSlug =
            SERVICE_LANDING_REGEX.exec(path)?.groups?.service_landing_slug;

          if (!translatedServiceLandingSlug) {
            throw new Error(`Could not parse service landing slug from path: ${path}`);
          }

          const fetchServiceLandingResponse: ServiceLanding = await get({
            countryUrlPrefix,
            translatedServiceLandingSlug,
          });

          dispatch(fetchServiceLandingSuccess(fetchServiceLandingResponse));
        } catch (e) {
          dispatch(fetchServiceLandingFailure((e as Error).message));
          captureError(e as Error);
        }
      },
    earlyPrefetch:
      () =>
      async (dispatch, getState, actions, { fetchServiceLanding }) => {
        await dispatch(serviceLinksEffects.earlyPrefetch());
        await dispatch(fetchServiceLanding());
      },
  },
  selectors: {
    getServiceLanding: (getState, createSelector) =>
      createSelector([getState], (x: State) => x.fetchServiceLandingResponse),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
